/**
 * MembershipCard.js — Lightning Rock Winery wine-club membership card (cardType "membership").
 *
 * Lightning Rock Winery override of the base MembershipCard. The old Lightning Rock Winery card is
 * intentionally SIMPLE: cover image, uppercase serif title, the
 * detailed_description, a "Learn more" button, and a "Join Now" CTA pinned
 * bottom-right. It does NOT render the membership type ("Membership"),
 * bottles/curated-by/shipping meta lines, or a "x% Member Discount" price — the
 * base card shows all of those, which is exactly why Lightning Rock Winery needs this override
 * to match the old.
 *
 * DOM parity with old createWineMembership (.card-box):
 *   cover → title → inner (desc, Learn more, Join Now footer)
 *
 * LEARN MORE: emits ws-prompt-select with the card's `message` (old cardClicked
 * -> sendMessage), echoed as a user turn for the detailed view.
 *
 * JOIN NOW: always rendered (old createWineMembership never gated on
 * display_button / cta_link) — solid maroon CTA bottom-right.
 *
 * Reference: RedConcierge/static/javascript/main.js createWineMembership() +
 *   index.css .card-box-title / .learn-more / .innerfooter.
 */

import { emit } from "../../../core/state.js";
import { getDefaultCardLogo } from "../../../core/defaultCardLogos.js";
import { CHECK_ICON, esc } from "../../../components/base/MembershipCard.js";

// Book/card glyph — same as the base MembershipCard's Learn-more icon.
const LEARN_ICON =
  '<svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2.67 13V3a1.67 1.67 0 0 1 1.66-1.67H13v13.34H4.33A1.67 1.67 0 0 1 2.67 13zm0 0a1.67 1.67 0 0 1 1.66-1.67H13"/></svg>';

export default class MembershipCard {
  render(club) {
    const card = document.createElement("div");
    card.className = "ws-card ws-membership-card";

    const openDetail = () => {
      const label =
        club.message || `Tell me more about ${club.title || "this membership"}`;
      emit("ws-prompt-select", {
        label,
        value: label,
        uniqueId: club.unique_id,
      });
    };

    // Cover image (clickable → detail). Old fell back to the padded house logo
    // when no cover image was set.
    const head = document.createElement("div");
    head.className = "ws-card-head";
    const img = document.createElement("img");
    img.className = "ws-card-img";
    img.loading = "lazy";
    img.alt = club.title || "Membership";
    img.style.cursor = "pointer";
    img.addEventListener("click", openDetail);
    if (club.cover_image_link) {
      img.src = club.cover_image_link;
    } else {
      img.src = getDefaultCardLogo();
      img.classList.add("ws-fg-mem-logo");
    }
    head.appendChild(img);
    card.appendChild(head);

    // Title sits between cover and inner panel (old .card-box-title).
    if (club.title) {
      const title = document.createElement("p");
      title.className = "ws-card-title";
      title.title = String(club.title).replace(/<br\s*\/?>/gi, " ");
      // Old rendered the title via innerHTML, so an admin-entered <br> in the
      // title (e.g. "Six Bottle<br>Selection") became a line break. textContent
      // would show it literally, so convert <br> → newline and let CSS
      // (white-space: pre-line) render the break — without injecting raw HTML.
      title.textContent = String(club.title).replace(/<br\s*\/?>/gi, "\n");
      card.appendChild(title);
    }

    // Old .innercardbox — desc + Learn more + Join Now footer.
    const body = document.createElement("div");
    body.className = "ws-card-body ws-fg-mem-body";

    const inner = document.createElement("div");
    inner.className = "ws-rich-inner";

    const bullets = Array.isArray(club.description)
      ? club.description.filter(Boolean)
      : club.description ? [club.description] : [];
    if (bullets.length) {
      const list = document.createElement("ul");
      list.className = "ws-rich-bullets";
      bullets.forEach((text) => {
        const li = document.createElement("li");
        li.innerHTML = `<span class="ws-rich-check">${CHECK_ICON}</span><span>${esc(text)}</span>`;
        list.appendChild(li);
      });
      inner.appendChild(list);
    }


    const learn = document.createElement("button");
    learn.type = "button";
    learn.className = "ws-card-learnmore";
    learn.innerHTML = `<span class="ws-learnmore-icon">${LEARN_ICON}</span><span>Learn more</span>`;
    learn.addEventListener("click", openDetail);
    body.appendChild(learn);

    // Footer: Join Now always bottom-right (old .innerfooter — always emitted
    // with cta_text / cta_link; never gated on display_button).
    const footer = document.createElement("div");
    footer.className = "ws-lr-mem-footer";
    const cta = document.createElement("a");
    cta.className = "ws-card-cta ws-fg-mem-cta";
    if (club.cta_link) {
      cta.href = club.cta_link;
      cta.target = "_blank";
      cta.rel = "noopener noreferrer";
    }
    cta.textContent = club.cta_text || "Join Now";
    footer.appendChild(cta);
    body.appendChild(footer);

    card.appendChild(inner);
    card.appendChild(body);
    return card;
  }
}
